<script lang="ts">
  import CartWidgetComponentV2 from "@retailer/svelte/CartWidget/v2/CartWidgetComponentV2.svelte";
  import * as pluralize from "@shared/svelte/Modules/pluralize";
  import { startCase, toLower } from "lodash";
  import { debounce } from "@shared/svelte/utils";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import { InfoCircleSolid } from "@getprovi/craft-svelte-icons";
  import { Icon } from "@getprovi/craft-svelte";
  import { isSupplier } from "@shared/services/internal_user_check_helpers.js";
  import { createEventDispatcher } from "svelte";

  import {
    CartItemResponse,
    CartProductUpdateParams,
    StockLevels,
  } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import CartWidgetStockInfo from "@retailer/svelte/CartWidget/v2/CartWidgetStockInfo.svelte";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";
  import InCartWithAlternativeFulfillmentMethod from "@retailer/svelte/CartWidget/v2/InCartWithAlternativeFulfillmentMethod.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import currentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
  import CartWidgetConnectAccountCta from "./CartWidgetConnectAccountCta.svelte";
  import CartWidgetMinimumOrderQuantityBadge from "./CartWidgetMinimumOrderQuantityBadge.svelte";
  import CartWidgetMinimumOrderQuantityErrorMessage from "./CartWidgetMinimumOrderQuantityErrorMessage.svelte";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let displayAsRow: boolean = false;
  export let fulfillmentMethod: RetailerFulfillmentMethod = null;
  export let widgetItem: CartItemResponse;
  export let showFulfillmentIndicator: boolean = false;
  export let tooltipPosition: string = "fizz-tooltip-center";
  export let tooltipMaxWidth: string = "300px";
  export let fulfillmentWarehouseId: number | null = null;
  export let showAltFulfillmentNotice: boolean = true;
  export let orderFulfillmentWarehouse: { id: number | null; name: string | null } = null;

  const DEFAULT_MAX = 999;

  const dispatch = createEventDispatcher();

  const onUpdateQuantity = debounce(async (event: CustomEvent) => {
    let newCaseQuantity = caseQuantity;
    let newUnitQuantity = unitQuantity;

    if (event.detail.quantityType === "case") {
      const totalCaseQuantity =
        distributorSummary.id === OHLQ_DISTRIBUTOR_ID
          ? event.detail.newQuantity * productSummary.container_case_size
          : event.detail.newQuantity;
      if (totalCaseQuantity + newUnitQuantity > DEFAULT_MAX) {
        newCaseQuantity =
          distributorSummary.id === OHLQ_DISTRIBUTOR_ID
            ? Math.floor((DEFAULT_MAX - newUnitQuantity) / productSummary.container_case_size)
            : DEFAULT_MAX;
      } else {
        newCaseQuantity = event.detail.newQuantity;
      }
    }

    if (event.detail.quantityType === "unit") {
      const quantityToCheck =
        distributorSummary.id === OHLQ_DISTRIBUTOR_ID
          ? newCaseQuantity * productSummary.container_case_size + event.detail.newQuantity
          : event.detail.newQuantity;
      if (quantityToCheck > DEFAULT_MAX) {
        newUnitQuantity = DEFAULT_MAX;
      } else {
        newUnitQuantity = event.detail.newQuantity;
      }
    }

    if (
      widgetItem.quantity_restrictions?.enforce &&
      totalUnits(newCaseQuantity, newUnitQuantity) > limit
    ) {
      let units: number;
      let cases: number;

      if (caseOption && unitOption) {
        cases = Math.floor(limit / unitsPerCase);
        units = limit % unitsPerCase;
      } else if (caseOption) {
        cases = limit;
      } else {
        units = limit;
      }

      newCaseQuantity = cases;
      newUnitQuantity = units;
    }

    if (caseQuantity === newCaseQuantity && unitQuantity === newUnitQuantity) {
      return;
    }

    if (event.detail.oldQuantity >= event.detail.newQuantity) {
      // When removing items from the cart, the order may be removed
      // sending the tracking event before cart updates to capture
      // the order information
      dispatch("cartWidgetChange", event.detail);
      await updateCartQuantity(newCaseQuantity, newUnitQuantity);
    } else {
      // When adding items to the cart, the order may not yet
      // exist sending the tracking event after cart updates
      // ensures order information will be available for tracking
      await updateCartQuantity(newCaseQuantity, newUnitQuantity);
      dispatch("cartWidgetChange", event.detail);
    }
  }, 500);

  const updateCartQuantity = async (newCaseQuantity: number, newUnitQuantity: number) => {
    const cartProduct: CartProductUpdateParams = {
      inventory_id: widgetItem.inventory_id,
      case_quantity: newCaseQuantity,
      unit_quantity: newUnitQuantity,
    };

    if (canUseWineCellar && distributorSummary.uses_warehouse_pickup)
      cartProduct.warehouse_id = fulfillmentWarehouseId;

    disabled = true;
    await InventoryItemStore.addToCart(widgetItem.inventory_id, cartProduct);
    await CartStore.load();
    if (newCaseQuantity === 0 && newUnitQuantity === 0) {
      dispatch("productRemoved");
    }
    disabled = false;
  };

  const totalUnits = (caseQuantity: number, unitQuantity: number): number => {
    if (!caseOption) {
      return unitQuantity;
    }
    return unitQuantity + caseQuantity * unitsPerCase;
  };

  type FulfillmentIndicator = { type: "Deliver" | "Pickup"; text: string };
  const pickupIndicator = (warehouseName: string): FulfillmentIndicator => {
    return { type: "Pickup", text: `at ${startCase(toLower(warehouseName))}` };
  };
  const deliveryIndicator = (retailerName: string): FulfillmentIndicator => {
    return { type: "Deliver", text: `to ${retailerName}` };
  };
  const buildFulfillmentIndicator = (
    fulfillmentMethod: RetailerFulfillmentMethod,
    retailerName: string,
  ): FulfillmentIndicator => {
    if (orderFulfillmentWarehouse) {
      return orderFulfillmentWarehouse.name
        ? pickupIndicator(orderFulfillmentWarehouse.name)
        : deliveryIndicator(retailerName);
    } else if (fulfillmentMethod?.warehouse) {
      return pickupIndicator(fulfillmentMethod.warehouse.name);
    } else {
      return deliveryIndicator(retailerName);
    }
  };

  const columnClasses = "fizz-full-width fizz-flow-8";
  const rowClasses = "fizz-flex-row-8-responsive";

  const cartWidgetPickupDeliveryExperienceEnabled = stagedRolloutCheck(
    "marketplace__pickup_delivery_cart_widget",
  );

  const widgetAtDefaultMax = (quantity_type: string): boolean => {
    if (distributorSummary.id === OHLQ_DISTRIBUTOR_ID) {
      const atMax = unitQuantity + caseQuantity * productSummary.container_case_size >= DEFAULT_MAX;
      return quantity_type === "case"
        ? atMax ||
            unitQuantity + (caseQuantity + 1) * productSummary.container_case_size > DEFAULT_MAX
        : atMax;
    } else {
      return quantity_type === "case" ? caseQuantity >= DEFAULT_MAX : unitQuantity >= DEFAULT_MAX;
    }
  };

  const minimumOrderQuantityNotMet = (quantityType: string): boolean => {
    if (quantityType === "case" && !caseQuantity) return false;
    if (quantityType === "unit" && !unitQuantity) return false;

    const { minimum_order_quantity: minimumOrderQuantity, product_summary: productSummary } =
      widgetItem;

    if (
      !minimumOrderQuantity ||
      (!minimumOrderQuantity.units && !minimumOrderQuantity.cases && !minimumOrderQuantity.sleeves)
    )
      return false;

    const totalQuantity = caseQuantity * productSummary.container_case_size + unitQuantity;
    return productSummary.full_sleeve_only
      ? minimumOrderQuantity.sleeves > totalQuantity
      : minimumOrderQuantity.units > totalQuantity;
  };

  const displayFulfillmentIndicator = (): boolean => {
    return (
      cartWidgetPickupDeliveryExperienceEnabled &&
      showFulfillmentIndicator &&
      widgetItem.distributor_summary?.uses_warehouse_pickup &&
      widgetItem.distributor_summary?.fulfillment_model !== "delivery_and_single_pickup_location" &&
      showStockInfo
    );
  };

  const fulfillmentMethodsStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__fulfillment_methods",
  );

  $: disabled = isSupplier();
  $: caseOption = widgetItem.buying_options.find(option => option.quantity_type === "case");
  $: caseQuantity = caseOption?.quantity || 0;
  $: unitOption = widgetItem.buying_options.find(option => option.quantity_type === "unit");
  $: unitQuantity = unitOption?.quantity || 0;
  $: limit = widgetItem.quantity_restrictions?.unit_limit;
  $: unitsPerCase = widgetItem.quantity_restrictions?.units_per_case || 0;
  $: limitReached =
    widgetItem.quantity_restrictions?.enforce && totalUnits(caseQuantity, unitQuantity) >= limit;
  $: errorNotifications = widgetItem.notices?.filter(notice => notice.level === "error");
  $: distributorSummary = widgetItem.distributor_summary;
  $: productSummary = widgetItem.product_summary;
  $: stockInfo = widgetItem.stock_info;
  $: availableToBuy = !!widgetItem.buying_options?.length;
  $: showAltFulfillment =
    showAltFulfillmentNotice &&
    cartWidgetPickupDeliveryExperienceEnabled &&
    widgetItem.fulfillment_summary &&
    widgetItem.fulfillment_summary?.id !== fulfillmentWarehouseId;
  $: fulfillmentIndicator = buildFulfillmentIndicator(
    fulfillmentMethod,
    $currentRetailerStore?.name,
  );
  $: canUseWineCellar =
    fulfillmentMethodsStagedRolloutEnabled && widgetItem?.distributor_summary?.uses_wine_cellar;
  $: needsAccountNumber = widgetItem?.stock_info?.additional_action?.endsWith("connect_account");
  $: showStockInfo = !canUseWineCellar || (canUseWineCellar && !needsAccountNumber);
  $: minimumOrderQuantity = widgetItem?.minimum_order_quantity;
  $: minimumOrderQuantityLabel = minimumOrderQuantity?.label;
</script>

<style>
  .max-quantity-error {
    border: 1px solid #821613;
  }
  .cart-widget-component-row-wrapper {
    min-height: 75px;
  }
</style>

{#if showAltFulfillment}
  <InCartWithAlternativeFulfillmentMethod
    buyingOptions={widgetItem.buying_options}
    fulfillmentSummary={widgetItem.fulfillment_summary}
    inventoryId={widgetItem.inventory_id}
  />
{:else if availableToBuy}
  {#if displayFulfillmentIndicator()}
    <div
      data-testid={`fulfillment-indicator-${widgetItem.inventory_id}`}
      class="fizz-text-subdued fizz-truncate"
      style="--max-width: 200px"
    >
      <span class="fizz-text-strong">{fulfillmentIndicator.type}</span>
      {fulfillmentIndicator.text}
    </div>
  {/if}
  {#if showStockInfo}
    <CartWidgetStockInfo
      {stockInfo}
      {distributorSummary}
      {productSummary}
      {availableToBuy}
      {tooltipPosition}
      {tooltipMaxWidth}
    />
  {:else}
    <CartWidgetConnectAccountCta selectedDistributor={widgetItem?.distributor_summary} />
  {/if}
  {#if minimumOrderQuantityLabel}
    <CartWidgetMinimumOrderQuantityBadge
      {minimumOrderQuantity}
      productId={productSummary.id}
      distributorId={distributorSummary.id}
      sku={widgetItem.sku}
    />
  {/if}
  <div
    data-testid={`cart-widget-${widgetItem.inventory_id}`}
    class={displayAsRow ? rowClasses : columnClasses}
  >
    {#each widgetItem.buying_options as option}
      <div class:cart-widget-component-row-wrapper={displayAsRow}>
        <div
          class={widgetAtDefaultMax(option.quantity_type) ? "max-quantity-error fizz-stack-8" : ""}
        >
          <CartWidgetComponentV2
            {option}
            {disabled}
            disableIncrement={limitReached || widgetAtDefaultMax(option.quantity_type)}
            inventoryId={widgetItem.inventory_id}
            containerType={pluralize.singular(option.label)}
            stockLevel={widgetItem?.stock_info?.level}
            on:updateQuantity
            on:updateQuantity={onUpdateQuantity}
            on:productRemoved
          />
        </div>
        {#if widgetAtDefaultMax(option.quantity_type)}
          <div
            class="fizz-flex-row fizz-text-small fizz-text-negative max-quantity-error-message align-left"
          >
            <Icon iconName={InfoCircleSolid} size="icon-base" class="fizz-inline-4" />
            <span class="fizz-inline-4">Max quantity is 999</span>
          </div>
        {/if}
        {#if minimumOrderQuantityNotMet(option.quantity_type)}
          <CartWidgetMinimumOrderQuantityErrorMessage
            {minimumOrderQuantity}
            productId={productSummary.id}
            distributorId={distributorSummary.id}
            sku={widgetItem.sku}
          />
        {/if}
      </div>
    {/each}
  </div>
{:else if [StockLevels.outOfStock, StockLevels.notSoldAtThisLocation, StockLevels.temporarilyUnavailable, StockLevels.noRecentUpdate, StockLevels.allocated].includes(stockInfo.level) && !errorNotifications.length}
  {#if showStockInfo}
    <CartWidgetStockInfo
      {stockInfo}
      {distributorSummary}
      {productSummary}
      {availableToBuy}
      {tooltipPosition}
      {tooltipMaxWidth}
    />
  {:else}
    <CartWidgetConnectAccountCta selectedDistributor={widgetItem?.distributor_summary} />
  {/if}
{/if}
